import { MapPin, Video } from "lucide-react";
import { formatSlotTime, type SlotLike } from "@/utils/appointments";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import { SLOT_BTN_IN_PERSON, SLOT_BTN_TELEHEALTH, IN_PERSON_BADGE, TELEHEALTH_BADGE } from "../constants";

type SlotRowProps<T extends SlotLike> = {
  date: string;
  slots: T[];
  location?: string;
  isTelehealth?: boolean;
  limit?: number;
  onBook: (slot: T) => void;
  onShowMore?: () => void;
  onLocationEnter?: () => void;
  onLocationLeave?: () => void;
};

export function SlotRow<T extends SlotLike>({
  date,
  slots,
  location,
  isTelehealth,
  limit,
  onBook,
  onShowMore,
  onLocationEnter,
  onLocationLeave,
}: SlotRowProps<T>) {
  const shown = limit ? slots.slice(0, limit) : slots;
  const remaining = slots.length - shown.length;
  const locatable = !!location && !isTelehealth && !!onLocationEnter;

  return (
    <div
      onMouseEnter={locatable ? onLocationEnter : undefined}
      onMouseLeave={locatable ? onLocationLeave : undefined}
      className={
        locatable
          ? "-mx-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-secondary/5"
          : undefined
      }
    >
      <h4 className="body-copy text-base mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="font-bold text-foreground">{date}</span>
        {location && (
          <span className="inline-flex items-center gap-1 text-sm font-normal text-muted-foreground">
            {isTelehealth ? (
              <Video className="h-3.5 w-3.5 shrink-0" />
            ) : (
              <MapPin className="h-3.5 w-3.5 shrink-0" />
            )}
            {location}
          </span>
        )}
      </h4>
      <div className="flex flex-wrap gap-2">
        {shown.map((slot) => (
          <Tooltip key={slot.id}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => onBook(slot)}
                className={slot.locationId ? SLOT_BTN_IN_PERSON : SLOT_BTN_TELEHEALTH}
              >
                {formatSlotTime(slot)}
              </button>
            </TooltipTrigger>
            <TooltipContent className="border-none bg-transparent p-0 shadow-none">
              {slot.locationId ? (
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${IN_PERSON_BADGE.className}`}>
                  {IN_PERSON_BADGE.label}
                </span>
              ) : (
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${TELEHEALTH_BADGE.className}`}>
                  {TELEHEALTH_BADGE.label}
                </span>
              )}
            </TooltipContent>
          </Tooltip>
        ))}
        {remaining > 0 &&
          (onShowMore ? (
            <button
              type="button"
              onClick={onShowMore}
              className="text-sm sm:text-base text-primary hover:text-primary/80 hover:underline self-center"
            >
              +{remaining} more
            </button>
          ) : (
            <span className="text-sm sm:text-base text-muted-foreground self-center">
              +{remaining} more
            </span>
          ))}
      </div>
    </div>
  );
}
